import { Flex } from '@repo/ui/Flex';

import { useRefundOptions } from '../../hooks/useRefundOptions';
import type { RefundOption } from '../../graphql/query/getRefundOptions';

import { SkeletonLoader } from '../SkeletonLoader/SkeletonLoader';
import { type ReactNode } from 'react';

import { Text } from '../Language/Language';
import { EmptyMessage } from '../EmptyMessage/EmptyMessage';
import s from './SummaryDisplay.module.css';

const SummaryContent = ({ summary }: { summary: RefundOption }) => (
  <Flex direction="column" padding="p-24" className={s.content}>
    {summary?.estimateBreakdown.map((estimate) => (
      <Flex
        justifyContent="space-between"
        key={estimate.label}
        className={s.lineItem}
      >
        <div className={s.summaryLabel}>{estimate.label}</div>
        <div className={s.summaryItem}>{estimate.formattedDisplay}</div>
      </Flex>
    ))}
  </Flex>
);

const Wrapper = ({ children }: { children: ReactNode }) => (
  <Flex className={s.container} gap="g-16" direction="column">
    <h2 className={s.header}>
      <Text langKey="Global.feeSummary.Header" />
    </h2>
    {children}
  </Flex>
);

const Loader = () => (
  <SkeletonLoader
    padding="p-16"
    direction="column"
    gap="g-24"
    layout={{
      repeat: 3,
      items: {
        default: [
          {
            columns: 4,
          },
          {
            columns: 4,
            startColumn: 9,
          },
        ],
        mobile: [
          {
            columns: 4,
          },
          {
            columns: 4,
            startColumn: 9,
          },
        ],
      },
    }}
  />
);

export const SummaryDisplay = ({
  footer,
}: {
  footer?: ReactNode;
} = {}) => {
  const { isLoading, refundOptions, selectedRefundOption } = useRefundOptions();
  const summary: RefundOption | undefined = refundOptions?.find(
    (option) => option.type === selectedRefundOption,
  );

  return (
    <Wrapper>
      {!summary && !selectedRefundOption && (
        <EmptyMessage
          language={{ langKey: 'Global.feeSummary.SelectOption' }}
        />
      )}
      {isLoading && <Loader />}
      {summary && !isLoading && <SummaryContent summary={summary} />}
      {footer}
    </Wrapper>
  );
};
